import { useMemo } from 'react';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { branchMissingMapLocation } from '../../settings/domain/branchLocationStatus';
import { getDefaultPosFiscalMode } from '../../fiscal/domain-api';
import { STOCK_SALE_POLICY_OPTIONS } from '../../reports/public-api';
import type { StoreActivationEvidence } from './useStoreActivationEvidence';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';

export type WeekOneSetupStepId = 'location' | 'essentials' | 'team' | 'printer' | 'mp';

export type WeekOneSetupStep = {
  id: WeekOneSetupStepId;
  label: string;
  hint: string;
  done: boolean;
  skipped?: boolean;
  optional?: boolean;
};

/**
 * Setup de la primera semana. Deriva de `useStoreActivationEvidence` (provider):
 * flags locales + home_snapshot + store_activation_snapshot cuando aplica.
 * No fetchea por su cuenta.
 */
export function useWeekOneSetup(userId: string | undefined, evidence: StoreActivationEvidence) {
  const branch = useSessionStore((s) => s.branches.find((b) => b.id === s.currentBranchId));
  const { entitlements } = usePlanEntitlements();
  const mpPlanAllowed = entitlements?.mp_qr_pos === true;
  const canPos = useHasBusinessCapability('pos');

  const lifetimeSaleCount =
    evidence.lifetimeSales ?? evidence.activation?.lifetimeSales ?? 0;
  const essentialsSaved = evidence.flags?.essentialsSaved === true;
  const teamSkipped = evidence.flags?.teamSkipped === true;
  const mpSkipped = evidence.flags?.mpSkipped === true;
  const printerSkipped = evidence.flags?.printerSkipped === true;
  const printerReady = evidence.flags?.printerReady === true;
  const printerDone = !canPos || printerSkipped || printerReady;
  const hasBranchEmployee = (evidence.activation?.employeesOnBranch ?? 0) > 0;
  const hasPendingInvite = (evidence.activation?.pendingInvites ?? 0) > 0;
  const mpStatus = evidence.activation?.mp ?? null;
  const loading = evidence.loading;

  const locationDone = branch ? !branchMissingMapLocation(branch) : false;
  const teamDone = teamSkipped || hasBranchEmployee || hasPendingInvite;
  const mpReady = mpStatus?.oauth_connected === true && mpStatus?.pos_configured === true;
  const mpDone = !mpPlanAllowed || mpSkipped || mpReady;

  const stockSalePolicy = branch?.stockSalePolicy ?? 'block';
  const fiscalOptionInPlan = entitlements?.fiscal_enabled === true;
  const defaultPosFiscalMode = getDefaultPosFiscalMode(entitlements, fiscalOptionInPlan);
  const stockPolicyLabel =
    STOCK_SALE_POLICY_OPTIONS.find((o) => o.id === stockSalePolicy)?.label ?? 'Bloquear';
  const fiscalLabel =
    defaultPosFiscalMode === 'fiscal'
      ? 'Fiscal'
      : defaultPosFiscalMode === 'auto_by_payment'
        ? 'Según el cobro'
        : 'No fiscal';

  const steps = useMemo((): WeekOneSetupStep[] => {
    const rows: WeekOneSetupStep[] = [
      {
        id: 'location',
        label: 'Ubicación',
        hint: locationDone ? 'Listo' : 'Marcá dónde está el local.',
        done: locationDone,
      },
      {
        id: 'essentials',
        label: 'Cómo cobrás acá',
        hint: essentialsSaved ? `Stock: ${stockPolicyLabel} · ${fiscalLabel}` : 'Revisá stock y fiscal al cobrar.',
        done: essentialsSaved,
      },
      {
        id: 'team',
        label: 'Invitar colaboradores',
        hint: teamSkipped
          ? 'Omitido · Solo yo por ahora'
          : teamDone
            ? 'Listo'
            : 'Invitá colaboradores.',
        done: teamDone,
        skipped: teamSkipped,
        optional: true,
      },
    ];
    if (canPos) {
      rows.push({
        id: 'printer',
        label: 'Conectá tu impresora',
        hint: printerSkipped
          ? 'Omitido · Sin térmica'
          : printerReady
            ? 'Listo'
            : 'Si tenés térmica, configurala en Dispositivos.',
        done: printerDone,
        skipped: printerSkipped,
        optional: true,
      });
    }
    if (mpPlanAllowed) {
      let mpHint = 'Cobrá con QR en el POS';
      if (mpSkipped) {
        mpHint = 'Omitido · Cobro solo efectivo';
      } else if (mpReady) {
        mpHint = 'Listo';
      } else if (mpStatus?.oauth_connected && !mpStatus?.pos_configured) {
        mpHint = 'Falta elegir la caja QR';
      } else if (mpStatus && !mpStatus.oauth_connected) {
        mpHint = 'Conectá tu cuenta de Mercado Pago';
      }
      rows.push({
        id: 'mp',
        label: 'Mercado Pago QR',
        hint: mpHint,
        done: mpDone,
        skipped: mpSkipped,
        optional: true,
      });
    }
    return rows;
  }, [
    canPos,
    essentialsSaved,
    fiscalLabel,
    locationDone,
    mpDone,
    mpPlanAllowed,
    mpReady,
    mpSkipped,
    mpStatus,
    printerDone,
    printerReady,
    printerSkipped,
    stockPolicyLabel,
    teamDone,
    teamSkipped,
  ]);

  const allDone = steps.every((s) => s.done);
  const eligible = lifetimeSaleCount >= 1;

  return {
    loading,
    eligible,
    steps,
    allDone,
    mpPlanAllowed,
    mpStatus,
    reload: evidence.reload,
  };
}
